import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Bed, Users, Search, Check, ChevronRight } from 'lucide-react';
import { searchAvailableRooms, createWalkIn, validateCoupon } from '../services/adminService';
import { format } from 'date-fns';

const STEPS = ['Select Room', 'Guest Details', 'Confirm'];

const EMPTY_GUEST = {
  firstName: '', lastName: '', email: '', phone: '',
  address: '', city: '', documentType: 'NID', documentNumber: '',
};

const fmtMoney = (n) => `৳${Number(n).toLocaleString('en-BD', { minimumFractionDigits: 0 })}`;

export default function AdminBooking() {
  const { toast } = useToast();
  const location = useLocation();

  const [step, setStep] = useState(0);

  // Step 0 — search
  const [checkIn, setCheckIn]   = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [adults, setAdults]     = useState(1);
  const [rooms, setRooms]       = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [numRooms, setNumRooms] = useState(1);

  // Step 1 — guest
  const [guest, setGuest] = useState(EMPTY_GUEST);

  // Step 2 — confirm
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  // Discount
  const [discountMode, setDiscountMode] = useState('none'); // 'none' | 'coupon' | 'manual'
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [manualDiscType, setManualDiscType] = useState(0); // 0=Percent, 1=Flat
  const [manualDiscValue, setManualDiscValue] = useState('');

  // Auto-fill from calendar navigation: ?checkIn=YYYY-MM-DD&roomTypeId=N
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ci = params.get('checkIn');
    const rtId = params.get('roomTypeId');
    if (!ci) return;
    const next = new Date(ci);
    next.setDate(next.getDate() + 1);
    const co = next.toISOString().split('T')[0];
    setCheckIn(ci);
    setCheckOut(co);
    if (!rtId) return;
    const rtIdNum = parseInt(rtId, 10);
    setSearching(true);
    setSearched(false);
    setSelectedRoom(null);
    searchAvailableRooms(ci, co, 1)
      .then(data => {
        setRooms(data);
        setSearched(true);
        const match = data.find(r => r.roomTypeId === rtIdNum);
        if (match) { setSelectedRoom(match); setNumRooms(1); }
      })
      .catch(() => toast({ title: 'Failed to search rooms', variant: 'destructive' }))
      .finally(() => setSearching(false));
  }, []); // eslint-disable-line

  const nights = checkIn && checkOut
    ? Math.max(0, Math.ceil((new Date(checkOut) - new Date(checkIn)) / 86400000))
    : 0;

  const subTotal = selectedRoom ? selectedRoom.price * nights * numRooms : 0;
  const taxAmt   = selectedRoom ? Math.round(subTotal * (selectedRoom.taxPercent ?? 10) / 100 * 100) / 100 : 0;
  const scAmt    = selectedRoom ? Math.round(subTotal * (selectedRoom.serviceChargePercent ?? 0) / 100 * 100) / 100 : 0;
  const grandTotal = subTotal + taxAmt + scAmt;

  const handleSearch = async () => {
    if (!checkIn || !checkOut) { toast({ title: 'Select both dates', variant: 'destructive' }); return; }
    if (new Date(checkOut) <= new Date(checkIn)) { toast({ title: 'Check-out must be after check-in', variant: 'destructive' }); return; }
    setSearching(true);
    setSearched(false);
    setSelectedRoom(null);
    try {
      const data = await searchAvailableRooms(checkIn, checkOut, adults);
      setRooms(data);
      setSearched(true);
    } catch {
      toast({ title: 'Failed to search rooms', variant: 'destructive' });
    } finally {
      setSearching(false);
    }
  };

  const handleSelectRoom = (room) => {
    setSelectedRoom(room);
    setNumRooms(1);
  };

  const handleConfirmBooking = async () => {
    if (!guest.firstName || !guest.lastName) {
      toast({ title: 'First name and last name are required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const res = await createWalkIn({
        roomTypeId: selectedRoom.roomTypeId,
        numberOfRooms: numRooms,
        aDT: adults,
        cHD: 0,
        checkInDate: checkIn,
        checkOutDate: checkOut,
        firstName: guest.firstName,
        lastName: guest.lastName,
        email: guest.email || null,
        phone: guest.phone || null,
        address: guest.address || null,
        city: guest.city || null,
        documentType: guest.documentType || null,
        documentNumber: guest.documentNumber || null,
        couponCode: discountMode === 'coupon' && couponResult?.isValid ? couponCode.trim().toUpperCase() : null,
        manualDiscountType: discountMode === 'manual' && manualDiscValue ? manualDiscType : null,
        manualDiscountValue: discountMode === 'manual' && manualDiscValue ? parseFloat(manualDiscValue) : null,
      });
      setResult(res);
      setStep(2);
    } catch (e) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const computedDiscount = (() => {
    if (discountMode === 'coupon' && couponResult?.isValid) return couponResult.discountAmount;
    if (discountMode === 'manual') {
      const v = parseFloat(manualDiscValue) || 0;
      if (manualDiscType === 0) return Math.round(grandTotal * v / 100 * 100) / 100;
      return Math.min(grandTotal, v);
    }
    return 0;
  })();
  const discountedTotal = grandTotal - computedDiscount;

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    setCouponResult(null);
    try {
      const res = await validateCoupon(couponCode.trim().toUpperCase(), grandTotal);
      if (res.isValid) {
        setCouponResult(res);
      } else {
        setCouponError(res.error || 'Invalid coupon');
      }
    } catch (e) {
      setCouponError(e.message);
    } finally {
      setCouponLoading(false);
    }
  };

  const clearDiscount = () => {
    setDiscountMode('none');
    setCouponCode(''); setCouponResult(null); setCouponError('');
    setManualDiscValue(''); setManualDiscType(0);
  };

  const handleReset = () => {
    setStep(0); setCheckIn(''); setCheckOut(''); setAdults(1);
    setRooms([]); setSearched(false); setSelectedRoom(null);
    setNumRooms(1); setGuest(EMPTY_GUEST); setResult(null);
    clearDiscount();
  };

  const setGuestField = (f) => (e) => setGuest(prev => ({ ...prev, [f]: e.target.value }));

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-white">Walk-in Booking</h1>
        <p className="text-slate-400 text-sm mt-1">Create a booking directly for a walk-in guest</p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              i === step ? 'bg-blue-600 text-white'
              : i < step  ? 'bg-green-600/20 text-green-400'
              : 'bg-slate-700/50 text-slate-500'
            }`}>
              {i < step ? <Check className="h-3.5 w-3.5" /> : <span className="w-3.5 h-3.5 flex items-center justify-center text-xs">{i + 1}</span>}
              {s}
            </div>
            {i < STEPS.length - 1 && <ChevronRight className="h-4 w-4 text-slate-600 flex-shrink-0" />}
          </React.Fragment>
        ))}
      </div>

      {/* ─── STEP 0: Room Search ─── */}
      {step === 0 && (
        <div className="space-y-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Search className="h-4 w-4" /> Search Available Rooms
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">Check-in</Label>
                  <Input type="date" value={checkIn} min={new Date().toISOString().split('T')[0]}
                    onChange={e => setCheckIn(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">Check-out</Label>
                  <Input type="date" value={checkOut} min={checkIn || new Date().toISOString().split('T')[0]}
                    onChange={e => setCheckOut(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">Guests</Label>
                  <Input type="number" min={1} max={20} value={adults}
                    onChange={e => setAdults(parseInt(e.target.value) || 1)}
                    className="bg-slate-700 border-slate-600 text-white" />
                </div>
              </div>
              <Button onClick={handleSearch} disabled={searching} className="bg-blue-600 hover:bg-blue-700">
                {searching ? 'Searching...' : 'Search Rooms'}
              </Button>
            </CardContent>
          </Card>

          {searched && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Bed className="h-4 w-4" /> Available Rooms
                  <span className="text-slate-400 font-normal text-sm">({rooms.length} found)</span>
                </CardTitle>
                {nights > 0 && (
                  <CardDescription className="text-slate-400">
                    {format(new Date(checkIn), 'dd MMM')} → {format(new Date(checkOut), 'dd MMM')} · {nights} night{nights > 1 ? 's' : ''}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                {rooms.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Bed className="h-10 w-10 mx-auto mb-2 text-slate-600" />
                    <p>No rooms available for the selected dates.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {rooms.map((room) => {
                      const isSelected = selectedRoom?.roomTypeId === room.roomTypeId;
                      return (
                        <div
                          key={room.roomTypeId}
                          onClick={() => handleSelectRoom(room)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            isSelected ? 'border-blue-500 bg-blue-600/10' : 'border-slate-600 hover:border-slate-500 bg-slate-700/30'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-semibold text-white">{room.roomType}</h3>
                              <p className="text-xs text-slate-400">{room.availableUnits} units available</p>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-green-400">{fmtMoney(room.price)}</div>
                              <div className="text-xs text-slate-400">per night</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-400">
                            <span className="flex items-center gap-1"><Users className="h-3 w-3" />{room.capacity} guests max</span>
                            <span>{room.bedType} · {room.numberOfBeds} bed(s)</span>
                          </div>
                          {isSelected && (
                            <div className="mt-3 flex items-center gap-3">
                              <Label className="text-slate-300 text-xs">Rooms:</Label>
                              <div className="flex items-center gap-2">
                                <Button type="button" variant="outline" size="icon"
                                  onClick={(e) => { e.stopPropagation(); setNumRooms(n => Math.max(1, n - 1)); }}
                                  className="h-6 w-6 border-slate-600 text-slate-300">−</Button>
                                <span className="text-white text-sm font-medium w-4 text-center">{numRooms}</span>
                                <Button type="button" variant="outline" size="icon"
                                  onClick={(e) => { e.stopPropagation(); setNumRooms(n => Math.min(room.availableUnits, n + 1)); }}
                                  className="h-6 w-6 border-slate-600 text-slate-300">+</Button>
                              </div>
                              <span className="text-slate-400 text-xs">max {room.availableUnits}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {selectedRoom && (
            <div className="flex justify-end">
              <Button onClick={() => setStep(1)} className="bg-blue-600 hover:bg-blue-700">
                Continue to Guest Details <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ─── STEP 1: Guest Details ─── */}
      {step === 1 && (
        <div className="space-y-4">
          {/* Booking summary bar */}
          <Card className="bg-blue-600/10 border-blue-500/30">
            <CardContent className="p-4 flex flex-wrap gap-4 text-sm">
              <span className="text-blue-300"><strong className="text-white">{selectedRoom.roomType}</strong> × {numRooms}</span>
              <span className="text-blue-300">{format(new Date(checkIn), 'dd MMM')} → {format(new Date(checkOut), 'dd MMM')} ({nights} nights)</span>
              <span className="text-green-400 font-bold">{fmtMoney(grandTotal)}</span>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base">Lead Guest Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">First Name *</Label>
                  <Input value={guest.firstName} onChange={setGuestField('firstName')}
                    className="bg-slate-700 border-slate-600 text-white" placeholder="John" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">Last Name *</Label>
                  <Input value={guest.lastName} onChange={setGuestField('lastName')}
                    className="bg-slate-700 border-slate-600 text-white" placeholder="Doe" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">Email</Label>
                  <Input type="email" value={guest.email} onChange={setGuestField('email')}
                    className="bg-slate-700 border-slate-600 text-white" placeholder="john@example.com" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">Phone</Label>
                  <Input value={guest.phone} onChange={setGuestField('phone')}
                    className="bg-slate-700 border-slate-600 text-white" placeholder="+880..." />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">Address</Label>
                  <Input value={guest.address} onChange={setGuestField('address')}
                    className="bg-slate-700 border-slate-600 text-white" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">City</Label>
                  <Input value={guest.city} onChange={setGuestField('city')}
                    className="bg-slate-700 border-slate-600 text-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">Document Type</Label>
                  <Select value={guest.documentType} onValueChange={v => setGuest(p => ({ ...p, documentType: v }))}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="NID">NID</SelectItem>
                      <SelectItem value="Passport">Passport</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">Document Number</Label>
                  <Input value={guest.documentNumber} onChange={setGuestField('documentNumber')}
                    className="bg-slate-700 border-slate-600 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Discount */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm">Discount (Optional)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Mode toggle */}
              <div className="flex gap-2">
                {['none', 'coupon', 'manual'].map(mode => (
                  <button key={mode}
                    onClick={() => { clearDiscount(); setDiscountMode(mode); }}
                    className={`flex-1 py-1.5 rounded text-xs font-medium border transition-all ${
                      discountMode === mode
                        ? 'bg-blue-600 border-blue-500 text-white'
                        : 'border-slate-600 text-slate-400 hover:border-slate-500'
                    }`}>
                    {mode === 'none' ? 'No Discount' : mode === 'coupon' ? 'Coupon Code' : 'Manual Discount'}
                  </button>
                ))}
              </div>

              {/* Coupon mode */}
              {discountMode === 'coupon' && (
                couponResult?.isValid ? (
                  <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2">
                    <span className="text-green-400 text-sm font-medium">
                      {couponCode.toUpperCase()} — {fmtMoney(couponResult.discountAmount)} off
                    </span>
                    <button onClick={() => { setCouponCode(''); setCouponResult(null); setCouponError(''); }}
                      className="text-xs text-slate-400 hover:text-red-400">Remove</button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="flex gap-2">
                      <Input
                        value={couponCode}
                        onChange={e => setCouponCode(e.target.value.toUpperCase())}
                        onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()}
                        placeholder="Enter coupon code"
                        className="bg-slate-700 border-slate-600 text-white font-mono uppercase flex-1"
                      />
                      <Button onClick={handleApplyCoupon} disabled={couponLoading || !couponCode.trim()}
                        className="bg-blue-600 hover:bg-blue-700 shrink-0">
                        {couponLoading ? '...' : 'Apply'}
                      </Button>
                    </div>
                    {couponError && <p className="text-red-400 text-xs">{couponError}</p>}
                  </div>
                )
              )}

              {/* Manual discount mode */}
              {discountMode === 'manual' && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <button onClick={() => setManualDiscType(0)}
                      className={`flex-1 py-1.5 rounded text-xs font-medium border transition-all ${
                        manualDiscType === 0 ? 'bg-blue-600 border-blue-500 text-white' : 'border-slate-600 text-slate-400'
                      }`}>
                      Percentage (%)
                    </button>
                    <button onClick={() => setManualDiscType(1)}
                      className={`flex-1 py-1.5 rounded text-xs font-medium border transition-all ${
                        manualDiscType === 1 ? 'bg-amber-600 border-amber-500 text-white' : 'border-slate-600 text-slate-400'
                      }`}>
                      Flat Amount (৳)
                    </button>
                  </div>
                  {/* Quick preset buttons */}
                  <div className="flex gap-2 flex-wrap">
                    {manualDiscType === 0
                      ? [10, 15, 20, 25].map(v => (
                        <button key={v} onClick={() => setManualDiscValue(String(v))}
                          className={`px-3 py-1 rounded text-xs border transition-all ${
                            manualDiscValue === String(v)
                              ? 'bg-blue-600 border-blue-500 text-white'
                              : 'border-slate-600 text-slate-400 hover:border-slate-500'
                          }`}>{v}%</button>
                      ))
                      : [500, 1000, 1500, 2000].map(v => (
                        <button key={v} onClick={() => setManualDiscValue(String(v))}
                          className={`px-3 py-1 rounded text-xs border transition-all ${
                            manualDiscValue === String(v)
                              ? 'bg-amber-600 border-amber-500 text-white'
                              : 'border-slate-600 text-slate-400 hover:border-slate-500'
                          }`}>৳{v}</button>
                      ))
                    }
                  </div>
                  <Input
                    type="number" min="0"
                    value={manualDiscValue}
                    onChange={e => setManualDiscValue(e.target.value)}
                    placeholder={manualDiscType === 0 ? 'Custom %' : 'Custom ৳'}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Price breakdown */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm">Price Breakdown</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between text-slate-300">
                <span>{fmtMoney(selectedRoom.price)} × {nights} nights × {numRooms} room(s)</span>
                <span>{fmtMoney(subTotal)}</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>Tax ({selectedRoom.taxPercent ?? 10}%)</span>
                <span>{fmtMoney(taxAmt)}</span>
              </div>
              {scAmt > 0 && (
                <div className="flex justify-between text-slate-300">
                  <span>Service Charge ({selectedRoom.serviceChargePercent}%)</span>
                  <span>{fmtMoney(scAmt)}</span>
                </div>
              )}
              {computedDiscount > 0 && (
                <div className="flex justify-between text-green-400">
                  <span>
                    {discountMode === 'coupon'
                      ? `Coupon (${couponCode.toUpperCase()})`
                      : `Manual Discount (${manualDiscType === 0 ? manualDiscValue + '%' : '৳' + manualDiscValue})`}
                  </span>
                  <span>−{fmtMoney(computedDiscount)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-white border-t border-slate-600 pt-2">
                <span>Grand Total</span>
                <span className="text-green-400">{fmtMoney(discountedTotal)}</span>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setStep(0)} className="border-slate-600 text-slate-300">Back</Button>
            <Button onClick={handleConfirmBooking} disabled={saving || !guest.firstName || !guest.lastName}
              className="bg-green-600 hover:bg-green-700">
              {saving ? 'Creating Booking...' : 'Confirm Booking'}
            </Button>
          </div>
        </div>
      )}

      {/* ─── STEP 2: Confirmation ─── */}
      {step === 2 && result && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto">
              <Check className="h-8 w-8 text-green-400" />
            </div>
            <h2 className="text-xl font-bold text-white">Booking Confirmed!</h2>
            <div className="bg-slate-700/50 rounded-xl p-6 text-left space-y-3 max-w-sm mx-auto">
              <div className="flex justify-between">
                <span className="text-slate-400 text-sm">PNR</span>
                <span className="font-mono text-blue-400 font-bold">{result.pnr}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400 text-sm">Guest</span>
                <span className="text-white text-sm">{result.guestName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400 text-sm">Room Type</span>
                <span className="text-white text-sm">{result.roomTypeName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400 text-sm">Check-in</span>
                <span className="text-white text-sm">{format(new Date(result.checkInDate), 'dd MMM yyyy')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400 text-sm">Check-out</span>
                <span className="text-white text-sm">{format(new Date(result.checkOutDate), 'dd MMM yyyy')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400 text-sm">Nights</span>
                <span className="text-white text-sm">{result.nights}</span>
              </div>
              {result.discountAmount > 0 && (
                <div className="flex justify-between">
                  <span className="text-slate-400 text-sm">
                    {result.couponCode ? `Coupon (${result.couponCode})` : 'Discount'}
                  </span>
                  <span className="text-green-400 text-sm font-medium">−{fmtMoney(result.discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-slate-600 pt-2">
                <span className="text-slate-400 text-sm font-medium">Total</span>
                <span className="text-green-400 font-bold">{fmtMoney(result.grandTotal)}</span>
              </div>
            </div>
            <div className="flex gap-3 justify-center pt-2">
              <Button onClick={handleReset} className="bg-blue-600 hover:bg-blue-700">New Booking</Button>
              <Button variant="outline" onClick={() => window.location.href = '/bookings'} className="border-slate-600 text-slate-300">
                View Bookings
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
